import React, {
  useEffect,
  useState
} from 'react';

import {
  FaSearch,
  FaEye,
  FaDownload,
  FaWallet
} from 'react-icons/fa';

import {
  getDeposits,
  exportDeposits
} from '../services/depositService';

import './Deposits.css';

const Deposits = () => {

  /*
  |--------------------------------------------------------------------------
  | STATES
  |--------------------------------------------------------------------------
  */

  const [loading, setLoading] = useState(false);

  const [deposits, setDeposits] = useState([]);

  const [analytics, setAnalytics] = useState({});

  const [pagination, setPagination] = useState({});

  const [page, setPage] = useState(1);

  const [search, setSearch] = useState('');

  /*
  |--------------------------------------------------------------------------
  | FETCH DEPOSITS
  |--------------------------------------------------------------------------
  */

  const fetchDeposits = async (
    currentPage = 1,
    searchValue = ''
  ) => {

    try {

      setLoading(true);

      const response = await getDeposits(
        currentPage,
        searchValue,
        10
      );

      if (response.status) {

        setDeposits(response.data || []);

        setAnalytics(response.analytics || {});

        setPagination(response.pagination || {});
      }

    } catch (error) {

      console.error(
        'Fetch deposits error:',
        error
      );

    } finally {

      setLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | INITIAL LOAD
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    fetchDeposits(page, search);

  }, [page]);

  /*
  |--------------------------------------------------------------------------
  | SEARCH
  |--------------------------------------------------------------------------
  */

  const handleSearch = async (e) => {

    const value = e.target.value;

    setSearch(value);

    setPage(1);

    fetchDeposits(1, value);
  };

  /*
  |--------------------------------------------------------------------------
  | STATUS COLORS
  |--------------------------------------------------------------------------
  */

  const getStatusColor = (status) => {

    switch (status?.toLowerCase()) {

      case 'success':
        return 'success';

      case 'pending':
        return 'warning';

      case 'failed':
        return 'danger';

      default:
        return 'secondary';
    }
  };

  /*
  |--------------------------------------------------------------------------
  | VIEW DETAILS
  |--------------------------------------------------------------------------
  */

  const handleViewDetails = (
    deposit
  ) => {

    console.log(
      'Deposit Details:',
      deposit
    );
  };

  return (

    <div className="deposits-page">

      {/* PAGE HEADER */}

      <div className="page-header">

        <h1>
          Deposit Management
        </h1>

        <p>
          Review and manage all deposit transactions
        </p>

      </div>

      {/* STATISTICS */}

      <div className="deposits-stats">

        <div className="stat-card">

          <div className="stat-icon">
            <FaWallet />
          </div>

          <div className="stat-info">

            <h4>
              Total Deposits
            </h4>

            <p>
              $
              {analytics.total_deposit_value || 0}
            </p>

          </div>

        </div>

        <div className="stat-card">

          <div className="stat-icon pending">
            <FaWallet />
          </div>

          <div className="stat-info">

            <h4>
              Pending Deposits
            </h4>

            <p>
              {analytics.total_no_of_pending_deposit || 0}
            </p>

          </div>

        </div>

        <div className="stat-card">

          <div className="stat-icon success">
            <FaWallet />
          </div>

          <div className="stat-info">

            <h4>
              Success Deposits
            </h4>

            <p>
              {analytics.total_no_of_success_deposit || 0}
            </p>

          </div>

        </div>

        <div className="stat-card">

          <div className="stat-icon danger">
            <FaWallet />
          </div>

          <div className="stat-info">

            <h4>
              Failed Deposits
            </h4>

            <p>
              {analytics.total_no_of_failed_deposit || 0}
            </p>

          </div>

        </div>

      </div>

      {/* CONTROLS */}

      <div className="deposits-controls">

        <div className="search-wrapper">

          <FaSearch className="search-icon" />

          <input
            type="text"
            placeholder="Search by wallet, amount, hash, date..."
            className="search-input"
            value={search}
            onChange={handleSearch}
          />

        </div>

        <button
          className="btn btn-export"
          onClick={() => exportDeposits(deposits)}
        >

          <FaDownload />

          Export

        </button>

      </div>

      {/* TABLE */}

      <div className="deposits-table-container">

        <div className="table-responsive">

          <table className="deposits-table">

            <thead>

              <tr>

                <th>
                  ID
                </th>

                <th>
                  User ID
                </th>

                <th>
                  Wallet
                </th>

                <th>
                  Amount
                </th>

                <th>
                  Asset
                </th>

                <th>
                  Txn Hash
                </th>

                <th>
                  Type
                </th>

                <th>
                  Status
                </th>

                <th>
                  Date
                </th>

                <th>
                  Action
                </th>

              </tr>

            </thead>

            <tbody>

              {!loading &&
                deposits.length > 0 ? (

                deposits.map((deposit) => (

                  <tr key={deposit.id}>

                    <td>
                      #{deposit.id}
                    </td>

                    <td>
                      {deposit.user_id}
                    </td>

                    <td>

                      <div className="wallet-address">

                        {deposit.from_address
                          ? `${deposit.from_address.slice(0, 6)}...${deposit.from_address.slice(-4)}`
                          : 'N/A'}

                      </div>

                    </td>

                    <td className="amount">

                      $
                      {deposit.deposit_amount}

                    </td>

                    <td>

                      <span className="currency-badge">

                        {deposit.deposit_asset?.toUpperCase()}

                      </span>

                    </td>

                    <td>

                      <div className="tx-hash">

                        {deposit.txn_hash?.length > 18
                          ? `${deposit.txn_hash.slice(0, 18)}...`
                          : deposit.txn_hash}

                      </div>

                    </td>

                    <td>
                      {deposit.deposit_type}
                    </td>

                    <td>

                      <span
                        className={`status-badge status-${getStatusColor(deposit.status)}`}
                      >

                        {deposit.status}

                      </span>

                    </td>

                    <td>
                      {deposit.date_time}
                    </td>

                    <td>

                      <div className="action-buttons">

                        <button
                          className="action-btn view"
                          onClick={() => handleViewDetails(deposit)}
                          title="View Details"
                        >

                          <FaEye />

                        </button>

                      </div>

                    </td>

                  </tr>
                ))

              ) : (

                <tr>

                  <td
                    colSpan="10"
                    className="text-center"
                  >

                    {loading
                      ? 'Loading deposits...'
                      : 'No deposits found'}

                  </td>

                </tr>
              )}

            </tbody>

          </table>

        </div>

      </div>

      {/* PAGINATION */}

      {pagination.last_page > 1 && (

        <div className="pagination-wrapper">

          <button
            disabled={!pagination.prev_page_url}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </button>

          <span>

            Page
            {' '}
            {pagination.current_page}
            {' '}
            of
            {' '}
            {pagination.last_page}

          </span>

          <button
            disabled={!pagination.next_page_url}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>

        </div>
      )}

    </div>
  );
};

export default Deposits;